"use client";

import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { renderDiscoverNoteSource } from "@/discover/sources/client/discover-client-source-registry";
import { groupBriefsByCategory } from "../../_lib/brief-grouping";
import { useExploreRoute } from "../../_lib/explore-route-context";
import type { ExploreBriefCard, ExploreBriefEvidenceRef } from "../../types";
import { type BriefSurface, usePlayModeTracking } from "../use-brief-analytics";

// ─── Constants ───────────────────────────────────────────────

const AUTO_ADVANCE_MS = 8000;

const CATEGORY_COLORS: Record<string, string> = {
  "video-and-film": "#dc2626",
  "image-and-design": "#8b5cf6",
  "audio-and-voice": "#d97706",
  "product-design": "#059669",
  "vibe-coding": "#6366f1",
  writing: "#78716c",
  other: "#78716c",
  "agentic-engineering": "#6366f1",
  "models-and-apis": "#d97706",
  "inference-and-infrastructure": "#8b5cf6",
  "security-and-reliability": "#dc2626",
  "cost-and-operations": "#059669",
  "research-and-benchmarks": "#0ea5e9",
  "business-and-policy": "#78716c",
};

function getCatColor({ slug }: { slug: string }): string {
  return CATEGORY_COLORS[slug] ?? "#78716c";
}

function ignoreNoteOpenChange() {
  // no-op: evidence embeds don't use open state
}

function NoteSourceEmbed({
  exploreBase,
  note,
}: {
  exploreBase: string;
  note: ExploreBriefEvidenceRef;
}) {
  return renderDiscoverNoteSource({
    exploreBase,
    note,
    onOpenChange: ignoreNoteOpenChange,
  });
}

// ─── Auto-advance hook ───────────────────────────────────────

function useAutoAdvance({
  active,
  duration,
  onAdvance,
}: {
  active: boolean;
  duration: number;
  onAdvance: () => void;
}) {
  const [fillProgress, setFillProgress] = useState(0);
  const startRef = useRef(0);
  const rafRef = useRef(0);

  useEffect(() => {
    if (!active) {
      setFillProgress(0);
      return;
    }
    startRef.current = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      setFillProgress(progress);
      if (progress >= 1) {
        onAdvance();
        startRef.current = now;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, duration, onAdvance]);

  return { fillProgress };
}

// ─── Segment width helper ─────────────────────────────────────

function computeSegmentScale({
  i,
  currentIndex,
  fillProgress,
}: {
  i: number;
  currentIndex: number;
  fillProgress: number;
}): number {
  if (i < currentIndex) {
    return 1;
  }
  if (i === currentIndex) {
    return fillProgress;
  }
  return 0;
}

// ─── Progress bar ────────────────────────────────────────────

function SegmentedProgressBar({
  total,
  currentIndex,
  fillProgress,
}: {
  total: number;
  currentIndex: number;
  fillProgress: number;
}) {
  return (
    <div style={{ display: "flex", gap: 3 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: 3,
            borderRadius: 2,
            background: "var(--explore-overlay-progress-track)",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              borderRadius: 2,
              background: "var(--explore-overlay-progress-fill)",
              transform: `scaleX(${computeSegmentScale({
                i,
                currentIndex,
                fillProgress,
              })})`,
              transformOrigin: "left center",
              transition: "transform 100ms linear",
              width: "100%",
            }}
          />
        </div>
      ))}
    </div>
  );
}

// ─── Brief card with evidence ────────────────────────────────

function BriefCard({
  brief,
  exploreBase,
}: {
  brief: ExploreBriefCard;
  exploreBase: string;
}) {
  const evidence =
    brief.evidence.length > 0
      ? brief.evidence
      : [{ sourceType: brief.sourceType, sourceId: brief.sourceId }];

  return (
    <div
      style={{
        width: "min(480px, calc(100vw - 2rem))",
        maxHeight: "calc(85vh - 120px)",
        overflowY: "auto",
        background: "var(--explore-overlay-card-bg)",
        borderRadius: 16,
        padding: "1.25rem",
        boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        fontFamily: "var(--homepage-sans-stack)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {brief.categoryDisplayName && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: "0.85rem" }}>{brief.categoryEmoji}</span>
          <span
            style={{
              fontSize: "0.75rem",
              color: "var(--explore-text-muted)",
              fontWeight: 500,
            }}
          >
            {brief.categoryDisplayName}
          </span>
          <span
            style={{ fontSize: "0.75rem", color: "var(--explore-text-muted)" }}
          >
            · {brief.timeAgo}
          </span>
        </div>
      )}
      <h3
        style={{
          fontSize: "1.05rem",
          fontWeight: 600,
          lineHeight: 1.35,
          color: "var(--explore-text-primary)",
          fontFamily: "var(--explore-card-title-stack)",
          margin: 0,
        }}
      >
        {brief.title}
      </h3>
      {brief.tldr && (
        <p
          style={{
            fontSize: "0.85rem",
            lineHeight: 1.6,
            color: "var(--explore-text-secondary)",
            background: "var(--explore-bg-tldr)",
            borderRadius: 8,
            padding: "0.75rem 1rem",
            margin: 0,
            fontFamily: "var(--reader-body-font-stack)",
          }}
        >
          {brief.tldr}
        </p>
      )}
      {/* Evidence (real tweets/sources) */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          overflow: "hidden",
        }}
      >
        {evidence.map((ev, i) => (
          <div
            key={`${ev.sourceType}:${ev.sourceId}-${i}`}
            style={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}
          >
            <NoteSourceEmbed exploreBase={exploreBase} note={ev} />
          </div>
        ))}
      </div>
      {brief.tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {brief.tags.map((tag) => (
            <span
              key={tag.slug}
              style={{
                background: "var(--explore-tag-bg)",
                color: "var(--explore-tag-text)",
                fontSize: "0.75rem",
                fontWeight: 500,
                padding: "4px 10px",
                borderRadius: 99,
              }}
            >
              {tag.displayName}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Export ──────────────────────────────────────────────────

interface BriefStoriesOverlayProps {
  briefs: ExploreBriefCard[];
  startIndex: number;
  onClose: () => void;
  onAllViewed?: () => void;
  /**
   * Surface that opened the overlay. Threaded into all
   * brief_play_mode_* events as `from_surface`. Defaults to the briefs
   * section.
   */
  fromSurface?: BriefSurface;
}

export function BriefStoriesOverlay({
  briefs,
  startIndex,
  onClose,
  onAllViewed,
  fromSurface = "explore_briefs_section",
}: BriefStoriesOverlayProps) {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [isPaused, setIsPaused] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { exploreBase, reportType: edition } = useExploreRoute();
  const { trackStarted, trackAdvanced, trackCompleted, trackDismissed } =
    usePlayModeTracking({
      edition,
      fromSurface,
    });
  const startedAtRef = useRef<number | null>(null);
  const completedRef = useRef(false);
  const prevIndexRef = useRef<number>(startIndex);
  const dismissMethodRef = useRef<"close_button" | "esc" | "backdrop">(
    "close_button"
  );
  // Source of the most recent index change. Each input path sets this *before*
  // calling goNext / goPrev / setCurrentIndex so the advance effect can attach
  // an accurate `via` to brief_play_mode_advanced. Defaults to "auto" because
  // the auto-advance timer is the only path that does not explicitly set it.
  const viaRef = useRef<"auto" | "tap" | "keyboard" | "swipe">("auto");

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only; fires started once and dismissed on unmount
  useEffect(() => {
    setMounted(true);
    startedAtRef.current = performance.now();
    trackStarted({ totalBriefs: briefs.length });
    return () => {
      if (!completedRef.current) {
        const elapsed = startedAtRef.current
          ? Math.round(performance.now() - startedAtRef.current)
          : 0;
        trackDismissed({
          position: prevIndexRef.current,
          total: briefs.length,
          dismissMethod: dismissMethodRef.current,
          timeOnPlayModeMs: elapsed,
        });
      }
    };
  }, []);

  const current = briefs[currentIndex];
  const catColor = getCatColor({ slug: current?.categorySlug ?? "" });
  const groups = useMemo(() => groupBriefsByCategory({ briefs }), [briefs]);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => {
      if (prev >= briefs.length - 1) {
        completedRef.current = true;
        const elapsed = startedAtRef.current
          ? Math.round(performance.now() - startedAtRef.current)
          : 0;
        trackCompleted({ total: briefs.length, timeOnPlayModeMs: elapsed });
        onAllViewed?.();
        onClose();
        return prev;
      }
      return prev + 1;
    });
  }, [briefs.length, onClose, onAllViewed, trackCompleted]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  useEffect(() => {
    if (currentIndex === prevIndexRef.current) {
      return;
    }
    const brief = briefs[currentIndex];
    if (brief) {
      trackAdvanced({
        brief,
        meta: {
          position: currentIndex,
          total: briefs.length,
          via: viaRef.current,
        },
      });
    }
    prevIndexRef.current = currentIndex;
    viaRef.current = "auto";
  }, [currentIndex, briefs, trackAdvanced]);

  const { fillProgress } = useAutoAdvance({
    active: !isPaused,
    duration: AUTO_ADVANCE_MS,
    onAdvance: goNext,
  });

  // Keyboard
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        viaRef.current = "keyboard";
        goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        viaRef.current = "keyboard";
        goPrev();
      } else if (e.key === "Escape") {
        dismissMethodRef.current = "esc";
        onClose();
      }
    };
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  }, [goNext, goPrev, onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  if (!(current && mounted)) {
    return null;
  }

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Gradient backdrop */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(160deg, ${catColor}30 0%, var(--explore-overlay-bg) 50%)`,
          backdropFilter: "blur(6px)",
          transition: "background 0.6s ease",
        }}
      />

      {/* Left side: prev or close */}
      <div
        onClick={
          currentIndex === 0
            ? () => {
                dismissMethodRef.current = "backdrop";
                onClose();
              }
            : () => {
                viaRef.current = "tap";
                goPrev();
              }
        }
        role="presentation"
        style={{
          position: "absolute",
          top: 80,
          bottom: 0,
          left: 0,
          width: "calc(50% - 260px)",
          minWidth: 40,
          zIndex: 10,
          cursor: "pointer",
        }}
      />
      {/* Right side: next or close */}
      <div
        onClick={
          currentIndex >= briefs.length - 1
            ? () => {
                dismissMethodRef.current = "backdrop";
                onClose();
              }
            : () => {
                viaRef.current = "tap";
                goNext();
              }
        }
        role="presentation"
        style={{
          position: "absolute",
          top: 80,
          bottom: 0,
          right: 0,
          width: "calc(50% - 260px)",
          minWidth: 40,
          zIndex: 10,
          cursor: "pointer",
        }}
      />

      {/* Header */}
      <div
        style={{
          position: "relative",
          zIndex: 20,
          padding: "1rem 1.25rem 0.5rem",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <SegmentedProgressBar
          currentIndex={currentIndex}
          fillProgress={fillProgress}
          total={briefs.length}
        />

        {/* Category chips — centered */}
        <div
          className="scrollbar-none"
          style={{
            display: "flex",
            gap: 6,
            overflowX: "auto",
            justifyContent: "center",
          }}
        >
          {groups.map((group) => {
            const isActive = group.categorySlug === current.categorySlug;
            return (
              <span
                key={group.categorySlug}
                style={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "5px 12px",
                  borderRadius: 99,
                  fontSize: "0.75rem",
                  fontWeight: isActive ? 700 : 500,
                  fontFamily: "var(--homepage-sans-stack)",
                  background: isActive
                    ? "var(--explore-overlay-chip-bg-active)"
                    : "transparent",
                  color: "var(--explore-overlay-text-on-media)",
                  transition: "all 0.2s",
                }}
              >
                <span>{group.categoryEmoji}</span>
                <span>{group.categoryDisplayName}</span>
              </span>
            );
          })}
        </div>

        {/* Counter + controls */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
          }}
        >
          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "var(--explore-overlay-text-on-media-muted)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {currentIndex + 1} of {briefs.length}
          </span>
          <button
            onClick={() => setIsPaused((p) => !p)}
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "none",
              background: "var(--explore-overlay-surface-frost)",
              backdropFilter: "blur(8px)",
              color: "var(--explore-overlay-text-on-media)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            type="button"
          >
            {isPaused ? (
              <svg
                fill="currentColor"
                height="14"
                viewBox="0 0 24 24"
                width="14"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            ) : (
              <svg
                fill="currentColor"
                height="14"
                viewBox="0 0 24 24"
                width="14"
              >
                <rect height="14" rx="1" width="4" x="6" y="5" />
                <rect height="14" rx="1" width="4" x="14" y="5" />
              </svg>
            )}
          </button>
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              right: 20,
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "none",
              background: "var(--explore-overlay-surface-frost)",
              backdropFilter: "blur(8px)",
              color: "var(--explore-overlay-text-on-media)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            type="button"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Centered card + nav arrows */}
      <div
        style={{
          position: "relative",
          zIndex: 5,
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 1rem",
        }}
      >
        {currentIndex > 0 && (
          <button
            onClick={() => {
              viaRef.current = "tap";
              goPrev();
            }}
            style={{
              position: "absolute",
              left: "max(16px, calc(50% - 280px))",
              zIndex: 15,
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "none",
              background: "var(--explore-overlay-surface-frost)",
              backdropFilter: "blur(8px)",
              color: "var(--explore-overlay-text-on-media)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            type="button"
          >
            <ChevronLeft size={20} />
          </button>
        )}

        <BriefCard brief={current} exploreBase={exploreBase} />

        {currentIndex < briefs.length - 1 && (
          <button
            onClick={() => {
              viaRef.current = "tap";
              goNext();
            }}
            style={{
              position: "absolute",
              right: "max(16px, calc(50% - 280px))",
              zIndex: 15,
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "none",
              background: "var(--explore-overlay-surface-frost)",
              backdropFilter: "blur(8px)",
              color: "var(--explore-overlay-text-on-media)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            type="button"
          >
            <ChevronRight size={20} />
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
